import React, { useEffect, useState } from "react";
import useApi from "../../../core/customhook/useApi";
import webservice_url from "../../../core/webservice/Webservice_url";
import { PaginationType } from "../../../types/common-type";
import Table from "../../../core/common/dataTable/index";
import { useParams } from "react-router";
import { cellWidth } from "../../../core/methods/tableCellWidth"; 
import Loading from "../../../core/common/custom-loading/Loading";
import { exportAntdTableToExcel } from "../../utils/exportExcel";
import { 
  safeNumberSort,
  safeStringSort,
} from "../../../core/methods/sorterMethods";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { getSearchableHeader } from "../../utils/getSearchableHeader";
import { filterByColumnSearch } from "../../utils/filterByColumnSearch";
import Datatable from "../../../core/common/dataTable/index";

const ValidCandidates = () => {
  const { orderId } = useParams(); 
  const [allItemList, setAllItemList] = useState<any[]>([]);
  const [inventoryColumnSearch, setInventoryColumnSearch] = useState<
    Record<string, string>
  >({});
  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000, 
    total: 0,
  });

  // API
  const { callApi: getValidApi, loading: loadingValid } = useApi<any>("", {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const filteredData = filterByColumnSearch(
    Array.isArray(allItemList) ? allItemList : [],
    inventoryColumnSearch,
  );

  const fetchValidData = async () => {
    try {
      const res = await getValidApi(
        {
          page: pagination?.current,
          limit: pagination?.pageSize,
        },
        `${webservice_url.getValidCandidateByOrder}/${orderId}`,
      );

      const data = res?.data || null;
      if (!data) return;

      setAllItemList(Array.isArray(data?.candidates) ? data?.candidates : []);
      setPagination((prev) => ({
        ...prev,
        total: data?.pagination?.total || 0,
      }));
    } catch (err: any) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!orderId) return;
    fetchValidData();
  }, [orderId, pagination?.current, pagination?.pageSize]);
 
  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      })); 
    }
  };

  // table column
  const columns: ColumnsType<any> = [
    {
      title: "Sr. No.",
      dataIndex: "sr_no",
      onCell: () => cellWidth(100),
      render: (text: string, record: any, index: number) => index + 1,
      sorter: false,
      excelFormatter: (text: string, record: any, index: number) => index + 1,
    } as any,
    {
      title: getSearchableHeader(
        "Candidate Name",
        "full_name",
        setInventoryColumnSearch,
      ),
      dataIndex: "full_name",
      onCell: () => cellWidth(200),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.full_name, b.full_name),
    },
    {
      title: getSearchableHeader(
        "Applicant Mobile Number",
        "phone",
        setInventoryColumnSearch,
      ),
      dataIndex: "phone",
      onCell: () => cellWidth(200),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeNumberSort(a.phone, b.phone),
    },
    {
      title: getSearchableHeader(
        "Applicant Email Id",
        "email",
        setInventoryColumnSearch,
      ), 
      dataIndex: "email",
      onCell: () => cellWidth(200),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.email, b.email),
    },
    {
      title: getSearchableHeader(
        "Applicant Registration Number/CRN",
        "crn",
        setInventoryColumnSearch,
      ), 
      dataIndex: "crn",
      onCell: () => cellWidth(200),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.crn, b.crn),
    },
    {
      title: getSearchableHeader(
        "Applicant Employee Id/Code",
        "employee_id",
        setInventoryColumnSearch,
      ), 
      dataIndex: "employee_id",
      onCell: () => cellWidth(200),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.employee_id, b.employee_id),
    },
    {
      title: getSearchableHeader(
        "PAN No.",
        "pan_no",
        setInventoryColumnSearch,
      ), 
      dataIndex: "pan_no",
      onCell: () => cellWidth(200),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.pan_no, b.pan_no),
    },
    {
      title: getSearchableHeader(
        "UAN No.",
        "uan_no",
        setInventoryColumnSearch,
      ), 
      dataIndex: "uan_no",
      onCell: () => cellWidth(200),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.uan_no, b.uan_no),
    },
    {
      title: getSearchableHeader(
        "Aadhar Address",
        "aadhar_address",
        setInventoryColumnSearch,
      ), 
      dataIndex: "aadhar_address",
      onCell: () => cellWidth(300),
      render: (text: any) => text || "-",
      sorter: (a, b) => safeStringSort(a.aadhar_address, b.aadhar_address),
    },
    {
      title: getSearchableHeader(
        "Aadhar Address Pin Code",
        "aadhar_address_pin_code",
        setInventoryColumnSearch,
      ), 
      dataIndex: "aadhar_address_pin_code",
      onCell: () => cellWidth(200),
      render: (text: any) => text || "-",
      sorter: (a, b) =>
        safeStringSort(a.aadhar_address_pin_code, b.aadhar_address_pin_code),
    },
    {
      title: getSearchableHeader(
        "Applicant Address",
        "applicant_address",
        setInventoryColumnSearch,
      ), 
      dataIndex: "applicant_address",
      onCell: () => cellWidth(300),
      render: (text: any) => text || "-",
      sorter: (a, b) =>
        safeStringSort(a.applicant_address, b.applicant_address),
    },
    {
      title: getSearchableHeader(
        "Applicant Address Pin Code",
        "applicant_address_pin_code",
        setInventoryColumnSearch,
      ), 
      dataIndex: "applicant_address_pin_code",
      onCell: () => cellWidth(200),
      render: (text: any) => text || "-",
      sorter: (a, b) =>
        safeStringSort(
          a.applicant_address_pin_code,
          b.applicant_address_pin_code,
        ),
    },
    {
      title: getSearchableHeader(
        "Joining Date",
        "joining_date",
        setInventoryColumnSearch,
      ),  
      dataIndex: "joining_date",
      onCell: () => cellWidth(200),
      render: (text: any) => {
        return <div>{text ? dayjs(text).format("DD-MM-YYYY") : "-"}</div>;
      },
      sorter: (a: any, b: any) =>
        dayjs(a.joining_date).valueOf() - dayjs(b.joining_date).valueOf(),
      excelFormatter: (text: string) =>
        text ? dayjs(text).format("DD-MM-YYYY") : "-",
    } as any,
  ];

  // handleDownloadExcel
  const handleDownloadExcel = async () => {
    exportAntdTableToExcel({
      columns,
      dataSource: allItemList,
      fileName: "valid_candidate_Report",
      sheetName: "Valid Candidates",
    });
  };

  return (
    <div className="mt-3 px-3 px-md-0">
      {/* Download Excel Button */}
      <div className="text-end">
        <button
          className="btn btn-sm btn-primary"
          onClick={handleDownloadExcel}
          disabled={loadingValid}
        >
          <i className="bi bi-download pe-2"></i>
          Export to Excel
        </button>
      </div>

      {loadingValid ? (
        <Loading />
      ) : (
        <div className="mt-3">
          {/* Table */}
          <Datatable
            dataSource={filteredData}
            columns={columns}
            Selection={false}
            // hideSizeChanger={true}
            fixedHeader
                tableHeight={650}
            pagination={pagination}
            handleTableChange={handleTableChange}
          />
        </div>
      )}
    </div>
  );
};

export default ValidCandidates;
